import React, { useContext, useState } from "react";
import { Table, Input, Button, Space, MenuProps, Dropdown, Typography } from "antd";
import { GroupTypeEnum, UserGroupTableRow } from "../../models/UserGroup";
import UserGroupService from "../../services/crud/UserGroupService";
import { ContainerOutlined, DownOutlined, LayoutOutlined, SwapOutlined, TeamOutlined } from "@ant-design/icons";
import { useUserGroupEditModal } from "../../hooks/userGroupEditModal";
import { OverlayContext } from "../../context/OverlayContext";
import { ToastContext } from "../../context/ToastContext";
import { useUserGroupContractEditModal } from "../../hooks/userGroupContractEditModal";
import UserGroupContractService from "../../services/crud/UserGroupContractService";
import { useNavigate } from "react-router-dom";
import { ContractsView } from "../../pages/admin/accounts/ContractsView";

function UserGroupsTable({
  accountId,
  accountName,
  userGroups = [], // array of UserGroupTableRow
  onActionComplete = () => {},
  groupType = GroupTypeEnum.APARTMENT,
  itemName = 'Lägenhet',
}: {
  userGroups: UserGroupTableRow[],
  accountId: number,
  accountName: string,
  onActionComplete: Function,
  groupType?: GroupTypeEnum,
  itemName?: string,
} ) {
  const { loading, loadMessage } = useContext(ToastContext);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filter, setFilter] = useState("");
  const filterTable = (data) => {
    const filterLc = filter.toLocaleLowerCase();
    return data.filter((item: UserGroupTableRow) => {
      const rowValue = `${item.userGroupLabel} ${item.userGroupDescription} ${item.userGroupContractMembers?.map((m) => m.username).join(' ')}`;
      return rowValue.toLocaleLowerCase().indexOf(filterLc) !== -1;
    });
  };
  const {openModal, closeModal} = useContext(OverlayContext);
  const {openCreateUserGroup, openEditUserGroup} = useUserGroupEditModal({
    itemName, onActionComplete,
  });
  const {openCreateUserGroupContract, openEditUserGroupContract} = useUserGroupContractEditModal({itemName: itemName, onActionComplete: onActionComplete})
  const navigate = useNavigate();

  const openTenancyTurnoverModal = (item: UserGroupTableRow) => {
    openCreateUserGroupContract({
      accountId: accountId,
      lastContractId: item.userGroupContractId,
      lastContractMembers: item.userGroupContractMembers.map((m) => m.username),
      lastContractStartDate: item.userGroupContractStartDate,
      userGroupId: item.userGroupId,
      userGroupLabel: item.userGroupLabel,
      //onAfterSave: async (addedContract: UserGroupContract) => {},
    });
  };
  const openEditContractModal = (item: UserGroupTableRow) => {
    loadMessage('Läser in ...', async () => {
      const ugc = await UserGroupContractService.getUserGroupContract(item.userGroupContractId);
      openEditUserGroupContract({
        accountId: accountId,
        userGroupLabel: item.userGroupLabel,
        userGroupContract: ugc,
        //onAfterSave: async (addedContract: UserGroupContract) => {},
      });
    });
  };

  const openViewContractsModal = (item: UserGroupTableRow) => {
    openModal({
      title: `${item.userGroupLabel} - Alla kontrakt`,
      content: <ContractsView accountId={accountId} userGroupId={item.userGroupId} />,
      hasCancel: false,
      okText: 'Close',
    });
  };

  const columns = [
    {
      title: "Lägenhet",
      dataIndex: "userGroupLabel",
      render: (text, record, index) => <div style={{whiteSpace: 'pre-wrap'}}>{`${text}\n${record.userGroupDescription}`.trim()}</div>,
      sorter: (a, b) => a.userGroupLabel.toLowerCase().localeCompare(b.userGroupLabel.toLowerCase()),
    },
    {
      title: "Hyresperiod",
      dataIndex: "userGroupContractStartDateAsDate",
      render: (text, record, index) => <div style={{whiteSpace: 'pre-wrap'}}>{
        `Inflytt: ${record.userGroupContractStartDateAsDate ?? '-'}\nUtflytt: ${record.userGroupContractEndDateAsDate ?? '-'}`
      }</div>,
      sorter: UserGroupTableRow.contractDateSort,
    },
    {
      title: "Boende",
      key: "members",
      render: (text, record:UserGroupTableRow, index) =>{
        const members = record?.userGroupContractMembers;
        return (
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>{members?.map((m) =>
            <Typography.Text style={{ cursor: "pointer", whiteSpace: 'pre' }} onClick={() => navigate(`/users/${record.id}`)}>
              {`${m.username}   ${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()}
            </Typography.Text>
          )}</div>);
      } ,
      sorter: UserGroupTableRow.memberEmailSort,
    },
    {
      title: "Åtgärder",
      key: "actions",
      render: (text, record, index) => {
        const hasContract = record.userGroupContractId !== null && record.userGroupContractId !== undefined;
        return (
          <>
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: "turnover",
                    label: hasContract ? "Omflytt" : "Första inflytt",
                    icon: <SwapOutlined />,
                    onClick: () => openTenancyTurnoverModal(record),
                  },
                  {
                    key: "edit-residents",
                    label: "Boende i befintligt kontrakt",
                    icon: <TeamOutlined />,
                    onClick: () => openEditContractModal(record),
                    disabled: !hasContract,
                  },
                  {
                    key: "view-contracts",
                    label: "Visa alla kontrakt",
                    icon: <ContainerOutlined />,
                    onClick: () => openViewContractsModal(record),
                    disabled: !hasContract,
                  },
                  {
                    key: "edit-apartment",
                    label: "Lägenhet",
                    icon: <LayoutOutlined />,
                    onClick: () => {
                      openEditUserGroup({
                        id: record.userGroupId,
                        label: record.userGroupLabel,
                      });
                    }
                  },
                ],
              }}
            >
              <Button
                //type="primary"
                size="middle"
              >
                Hantera <DownOutlined />
              </Button>
            </Dropdown>
          </>
        );
      },
    },
  ];

  const items: MenuProps["items"] = [
    {
      label: `Ta bort ${itemName}`,
      key: "REMOVE",
    },
  ];
  const onClick = async ({ key }) => {
    switch (key) {
      case "REMOVE":
        openModal({
          title: `Ta bort ${selectedRowKeys.length} valda ${itemName.toLocaleLowerCase()}er?`,
          content: <Typography.Text>
            Users in selected {itemName.toLocaleLowerCase()}s will be removed from {accountName}.
          </Typography.Text>,
          onOk: async () => {
            await Promise.allSettled(selectedRowKeys.map((id) => UserGroupService.deleteUserGroup(id)));
            closeModal();
            // Reload table
            onActionComplete();
          },
        });
        break;
    }
  };

  type ActionDropDownProps = {};
  const ActionsDropdown: React.FC<ActionDropDownProps> = ({}) => (
    <Dropdown menu={{ items, onClick }} trigger={["click"]}>
      <a>
        <Space>
          Åtgärder
          <DownOutlined />
        </Space>
      </a>
    </Dropdown>
  );

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div>
      <Typography.Title level={3}>Apartments</Typography.Title>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
      </div>
      <Space style={{ marginBottom: 10, justifyContent: "space-between", width: "100%" }}>
        <Space>
          <ActionsDropdown />
          <span style={{ marginLeft: 8 }}> {`(${selectedRowKeys.length} valda)`} </span>
          <Input.Search size="middle" placeholder={`Sök i tabellen ...`} style={{ width: 200, marginLeft: 30 }} onChange={(e) => setFilter(e.target.value)} />
        </Space>
        <Button type="primary" size="middle" onClick={() => openCreateUserGroup(accountId)}>
          Lägg till {itemName}
        </Button>
      </Space>
      <Table
        bordered
        rowSelection={rowSelection}
        rowKey="id"
        dataSource={filterTable(userGroups)}
        columns={columns}
        pagination={{ total: userGroups.length, defaultPageSize: 30, showSizeChanger: true }}
      />
    </div>
  );
};

export default UserGroupsTable;

